<template>
  <div class="planning-list">
    <template v-if="loaded">
      <template v-if="allowView">
        <!-- Planning list header -->
        <div v-if="!isListEmpty" class="list-header">
          <div class="list-title">{{ 'base.loads' | translate }}</div>
        </div>
        <div v-if="!isListEmpty" class="list" key="list">
          <draggable
            handle=".schedule-item-handle"
            v-model="schedulesList"
            @end="savePosition">
            <list-item
              @updated="loadData"
              @highlight-related="highlightItem"
              @unhighlight-related="unhighlightItem"
              v-for="(item, index) in schedules"
              :key="item.id"
              :item="item"
              :index="index"/>
          </draggable>
        </div>
        <list-empty v-else></list-empty>
      </template>
      <div class="table__empty" v-else>
        {{ 'perm.info.no_view' | translate }}
      </div>
    </template>
    <template v-else>
      <list-item-skeleton></list-item-skeleton>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import Draggable from 'vuedraggable'

import ListItem from './ListItem.vue'

import ListEmpty from './ListEmpty'
import ListItemSkeleton from './ListItemSkeleton'
import api from '@/api'
import { getFarmPath } from '@/util/app.js'
import { getDateForDatePicker } from '@/util'

export default {
  name: 'PlanningList',

  components: {
    Draggable,
    ListItem,
    ListItemSkeleton,
    ListEmpty
  },

  mounted () {
    this.loadData()
    // this.$store.dispatch('setPlanningUpdater', this.loadData)
  },

  data: () => ({
    loaded: false
  }),

  methods: {
    ...mapActions('schedule', [
      'loadSchedules',
      'sortSchedules'
    ]),
    ...mapMutations('schedule', [
      'SET_SCHEDULES'
    ]),

    highlightItem (itemId) {
      const item = this.schedules.find(i => i.id === itemId)
      if (item) {
        this.$set(item, '_isHighlighted', true)
      }
    },

    unhighlightItem (itemId) {
      const item = this.schedules.find(i => i.id === itemId)
      if (item) {
        this.$set(item, '_isHighlighted', false)
      }
    },

    async loadData () {
      if (this.allowView) {
        const params = {
          farm: this.$route.params.farm_id,
          loading: this.$route.query.loading,
          planned_on: this.$route.query.date
        }
        if (params.loading && params.planned_on) {
          this.$store.dispatch('planning/togglePlanningLoading', true)
          await this.loadSchedules(params)
          this.calcSomeFields()
        }
        this.$store.dispatch('planning/togglePlanningLoading', false)
        // Sort schedules by ordering..
        // this.schedules.sort((prev, next) => prev.ordering - next.ordering)
      }
      this.loaded = true
    },

    calcSomeFields () {
      this.schedules.forEach(element => {
        let animalCount = 0
        let totalWeight = 0
        let weightPerHead = 0
        let validGroupsCount = 0

        element.schedule_groups.forEach(group => {
          animalCount += group.animal_count

          if (group.animal_count > 0 && group.plan_weight > 0) {
            const calculatedWeightPerHead = group.plan_weight / group.animal_count
            if (!isNaN(calculatedWeightPerHead)) {
              weightPerHead += calculatedWeightPerHead
              validGroupsCount++
            }
          }

          totalWeight += group.plan_weight > 0 ? group.plan_weight : 0
        })

        element.weight_per_head = validGroupsCount > 0 ? weightPerHead / validGroupsCount : 0

        element.total_weight = totalWeight
        element.animal_count = animalCount
      })

      this.schedules.forEach(element => {
        element.schedule_ration.ingredients.forEach(ingredient => {
          ingredient.weight_per_head = element.animal_count > 0
            ? ingredient.plan_weight / element.animal_count
            : 0
        })
      })
    },

    // Change schedule ordering
    async savePosition () {
      const payload = {
        farm: this.$route.params.farm_id,
        planned_on: this.$route.query.date,
        loading: this.$route.query.loading,
        schedules: this.schedules.map((item, index) => ({
          id: item.id,
          ordering: index + 1
        }))
      }
      await this.sortSchedules(payload)
    }
  },

  computed: {
    ...mapGetters('schedule', [
      'schedules'
    ]),
    ...mapGetters('planning', [
      'getPlanningData'
    ]),
    planningData () {
      return this.getPlanningData
    },
    timezone () {
      return this.$store.getters['account/timezone']
    },
    schedulesList: {
      get () {
        return this.schedules
      },
      set (value) {
        this.SET_SCHEDULES(value)
      }
    },

    path () {
      return this.$route.path
    },

    date () {
      return this.$route.query.date
    },

    loading () {
      return this.$route.query.loading
    },

    isListEmpty () {
      return this.schedules.length === 0 && this.loaded
    },

    allowView () {
      return this.hasPermission('schedule', 'view')
    }
  },

  watch: {
    date () {
      this.loaded = false
      this.loadData()
    },

    loading (value) {
      if (this.loaded) {
        this.loaded = false
        this.loadData()
      }
    },

    schedules () {
      this.calcSomeFields()
    },
    async planningData (newVal) {
      const date = getDateForDatePicker(this.timezone)
      const params = {
        planned_on: date,
        farm: this.$route.params.farm_id
      }
      const { data, errors } = await api.schedule.loadings.list(params)
      if (!errors) {
        const basePath = getFarmPath(null, this.$route)
        const path = `${basePath}/planning?loading=${data[0].loading}&date=${date}`
        this.$router.replace(path)
        this.loadData()
      }
    }
  },
}
</script>

<style scoped>
  .list-header {
    display: flex;
    justify-content: space-between;
  }

  .list-title {
    font-size: 14px;
    padding-left: 10px;
    margin-bottom: 10px;
  }
</style>